import initializeIziButton from "./initializeIziButton";

let minicartObserverInitialized = false;
let minicartPlaceholderAppearanceObserverInitialized = false;
let minicartWidgetInitializationInProgress = false;

export default function refreshMinicartIziButton() {
  initMinicartRefreshHandler();
}

function initMinicartRefreshHandler() {
  if (minicartObserverInitialized) {
    return;
  }

  const placeholder = getMinicartPlaceholder();

  if (!placeholder) {
    watchForMinicartPlaceholderAppearance();
    return;
  }

  minicartObserverInitialized = true;
  watchMinicartElement(placeholder);
}

function watchForMinicartPlaceholderAppearance() {
  if (minicartPlaceholderAppearanceObserverInitialized) {
    return;
  }

  minicartPlaceholderAppearanceObserverInitialized = true;

  const observer = new MutationObserver((mutations, obs) => {
    for (const mutation of mutations) {
      for (const addedNode of mutation.addedNodes) {
        const placeholder = findMinicartPlaceholderInNode(addedNode);

        if (!placeholder) {
          continue;
        }

        obs.disconnect();
        minicartPlaceholderAppearanceObserverInitialized = false;

        initMinicartWidgetIfNeeded();

        return;
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

function watchMinicartElement(placeholder) {
  const observer = new MutationObserver((mutations, obs) => {
    for (const mutation of mutations) {
      for (const removedNode of mutation.removedNodes) {
        if (!isObservedMinicartPlaceholderRemoved(removedNode, placeholder)) {
          continue;
        }

        obs.disconnect();
        minicartObserverInitialized = false;

        if (document.querySelector("#inpostpay-widget-popup")) {
          waitForMinicartPlaceholder()
            .then((newPlaceholder) => {
              if (minicartObserverInitialized) {
                return;
              }

              minicartObserverInitialized = true;
              watchMinicartElement(newPlaceholder);
            })
            .catch(() => {
              minicartObserverInitialized = false;
              watchForMinicartPlaceholderAppearance();
            });

          return;
        }

        initMinicartWidgetIfNeeded();
        return;
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

async function initMinicartWidgetIfNeeded() {
  if (minicartWidgetInitializationInProgress) {
    return;
  }

  const minicartPlaceholder = getMinicartPlaceholder();

  if (!minicartPlaceholder) {
    watchForMinicartPlaceholderAppearance();
    return;
  }

  const parent = minicartPlaceholder.parentElement;
  if (parent && parent.querySelector("inpost-izi-button")) {
    if (!minicartObserverInitialized) {
      minicartObserverInitialized = true;
      watchMinicartElement(minicartPlaceholder);
    }

    return;
  }

  minicartWidgetInitializationInProgress = true;

  try {
    await waitForIppWidget();
    await initializeIziButton(".izi-widget-placeholder.izi-widget-minicart");

    const currentMinicartPlaceholder = getMinicartPlaceholder();

    if (currentMinicartPlaceholder && !minicartObserverInitialized) {
      minicartObserverInitialized = true;
      watchMinicartElement(currentMinicartPlaceholder);
    }

    if (!currentMinicartPlaceholder) {
      watchForMinicartPlaceholderAppearance();
    }
  } catch (e) {
    minicartObserverInitialized = false;

    const currentMinicartPlaceholder = getMinicartPlaceholder();

    if (currentMinicartPlaceholder) {
      if (!currentMinicartPlaceholder.parentElement?.querySelector("inpost-izi-button")) {
        console.warn("[InPostPay] Minicart widget could not be initialized:", e);
      }

      minicartObserverInitialized = true;
      watchMinicartElement(currentMinicartPlaceholder);
    } else {
      watchForMinicartPlaceholderAppearance();
    }
  } finally {
    minicartWidgetInitializationInProgress = false;
  }
}

function getMinicartPlaceholder() {
  return document.querySelector(".izi-widget-placeholder.izi-widget-minicart");
}

function findMinicartPlaceholderInNode(node) {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  if (node.matches(".izi-widget-placeholder.izi-widget-minicart")) {
    return node;
  }

  return node.querySelector(".izi-widget-placeholder.izi-widget-minicart");
}

function isObservedMinicartPlaceholderRemoved(removedNode, placeholder) {
  return (
    removedNode === placeholder ||
    (
      removedNode.nodeType === Node.ELEMENT_NODE &&
      removedNode.contains(placeholder)
    )
  );
}

function waitForMinicartPlaceholder(maxRetries = 20, delay = 100) {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const interval = setInterval(() => {
      const placeholder = getMinicartPlaceholder();

      if (placeholder) {
        clearInterval(interval);
        resolve(placeholder);
        return;
      }

      attempts++;

      if (attempts >= maxRetries) {
        clearInterval(interval);
        reject(new Error("Minicart placeholder not found"));
      }
    }, delay);
  });
}

function waitForIppWidget(maxRetries = 30, delay = 100) {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const interval = setInterval(() => {
      if (window.IPPwidget && typeof window.IPPwidget.refresh === "function") {
        clearInterval(interval);
        resolve();
        return;
      }

      attempts++;

      if (attempts >= maxRetries) {
        clearInterval(interval);
        reject(new Error("IPP widget instance is not ready"));
      }
    }, delay);
  });
}
